import { getFlagEmoji } from '@advanza/func'
import { Button } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import InputField from '@trustoo/components/input/InputField'
import {
    formatKeywordMappingCount,
    readAppliedFilter,
} from 'components/keywordMappings/keywordMappingBulk'
import KeywordMappingBulkModal from 'components/keywordMappings/KeywordMappingBulkModal'
import {
    KEYWORD_MAPPING_FOCUS_RING,
    KEYWORD_MAPPING_SEARCH_INTENTS_STORE,
    readPlaceholderNames,
    readServiceIds,
} from 'components/keywordMappings/keywordMappingModel'
import KeywordMappingServiceSelect from 'components/keywordMappings/KeywordMappingServiceSelect'
import TableList from 'components/TableList'
import { useListPerCountry } from 'hooks/listHooks'
import {
    KeywordMappingSearchIntent,
    keywordMappingSearchIntentRowId,
    readKeywordMappingSearchIntent,
} from 'interfaces/KeywordMappingSearchIntent'
import { hasAccess } from 'misc/user'
import { schema } from 'normalizr'
import { ChangeEvent, ReactNode, useEffect, useState } from 'react'

const useKeywordMappingSearchIntents = (filterId = 'all') =>
    useListPerCountry(filterId, {
        url: 'office/keyword-mappings/search-intent-overview',
        schema: [
            new schema.Entity(
                KEYWORD_MAPPING_SEARCH_INTENTS_STORE,
                {},
                { idAttribute: keywordMappingSearchIntentRowId },
            ),
        ],
        reducerKey: KEYWORD_MAPPING_SEARCH_INTENTS_STORE,
    })

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function isDifferentOnly(value: unknown): boolean {
    if (value === 1) return true
    if (value === '1') return true
    return false
}

function marketLabel(countryCode: string): string {
    if (countryCode === '') return ''
    return `${getFlagEmoji(countryCode)} ${countryCode}`
}

function keywordCountLabel(item: KeywordMappingSearchIntent): string {
    const active = formatKeywordMappingCount(item.activeKeywordCount)
    const total = formatKeywordMappingCount(item.keywordCount)
    return `${active} active of ${total}`
}

function renderH1Text(item: KeywordMappingSearchIntent): ReactNode {
    if (item.h1Text !== null) {
        return <span className="whitespace-pre-wrap">{item.h1Text}</span>
    }

    return (
        <span className="inline-flex rounded-full bg-[#fef3c7] px-2 py-0.5 text-xs font-semibold text-[#92400e]">
            {formatKeywordMappingCount(item.h1TextCount)} different H1 texts
        </span>
    )
}

const BASE_DEFINITIONS = {
    countryCode: { name: 'Market', noOrder: true },
    serviceName: { name: 'Service', noOrder: true },
    search_intent: { name: 'Search intent', noOrder: true },
    keywords: { name: 'Keywords', noOrder: true },
    h1: { name: 'H1 text', noOrder: true },
}

const KeywordMappingSearchIntents = () => {
    const hasWriteAccess = Boolean(hasAccess('keyword_mappings', 'w'))
    const listState = useKeywordMappingSearchIntents()
    const { entities, extra, filter, changeFilter } = listState
    const placeholders = readPlaceholderNames(extra)
    const appliedSearchIntent = readAppliedFilter(filter, 'searchIntent')
    const [searchIntentDraft, setSearchIntentDraft] = useState(appliedSearchIntent)

    useEffect(() => {
        setSearchIntentDraft(appliedSearchIntent)
    }, [appliedSearchIntent])

    const entityMap =
        isRecord(entities) && isRecord(entities.keywordMappingSearchIntents)
            ? entities.keywordMappingSearchIntents
            : {}
    const tableDefinitions = hasWriteAccess
        ? {
              ...BASE_DEFINITIONS,
              actions: { name: 'Actions', noOrder: true, preventRedirect: true },
          }
        : BASE_DEFINITIONS

    const applySearchIntent = (value: string) => {
        const next = value.trim()
        if (next === appliedSearchIntent) return
        changeFilter({ searchIntent: next === '' ? undefined : next, page: 0 })
    }

    const onDifferentOnlyChange = (event: ChangeEvent<HTMLInputElement>) => {
        changeFilter({
            differentOnly: event.target.checked ? 1 : undefined,
            page: 0,
        })
    }

    const openH1 = (item: KeywordMappingSearchIntent) => {
        const initialH1Text = item.h1Text === null ? '' : item.h1Text
        void NiceModal.show(KeywordMappingBulkModal, {
            action: 'h1',
            target: {
                group: { serviceId: item.service_id, searchIntent: item.search_intent },
            },
            h1Placeholders: placeholders,
            initialH1Text,
            groupKeywordCount: item.keywordCount,
        }).catch(() => undefined)
    }

    const renderRow = (id: string) => {
        const item = readKeywordMappingSearchIntent(entityMap[id])
        if (item === null) {
            return { cols: Object.keys(tableDefinitions).map(() => '') }
        }

        return {
            cols: Object.keys(tableDefinitions).map((key) => {
                if (key === 'countryCode') return marketLabel(item.countryCode)
                if (key === 'keywords') return keywordCountLabel(item)
                if (key === 'h1') return renderH1Text(item)
                if (key !== 'actions') {
                    const value = item[key as keyof KeywordMappingSearchIntent]
                    if (typeof value === 'string' || typeof value === 'number') return value
                    return ''
                }

                return (
                    <Button
                        type="button"
                        name="text"
                        className={KEYWORD_MAPPING_FOCUS_RING}
                        onClick={() => openH1(item)}>
                        Edit H1
                    </Button>
                )
            }),
        }
    }

    return (
        <>
            <div className="mb-4 flex flex-wrap items-end gap-4">
                <div className="w-64">
                    <KeywordMappingServiceSelect
                        name="serviceIds"
                        emptyOption="All services"
                        value={readServiceIds(filter.serviceIds)[0] ?? null}
                        onChange={(serviceId) =>
                            changeFilter({
                                serviceIds: serviceId === null ? [] : [serviceId],
                                page: 0,
                            })
                        }
                    />
                </div>
                <InputField
                    id="keyword-mapping-intent-filter"
                    name="searchIntent"
                    className="w-64"
                    legend="Search intent"
                    aria-label="Search intent"
                    value={searchIntentDraft}
                    inputClassName={KEYWORD_MAPPING_FOCUS_RING}
                    onChange={(event) => setSearchIntentDraft(event.target.value)}
                    onBlur={() => applySearchIntent(searchIntentDraft)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') applySearchIntent(searchIntentDraft)
                    }}
                />
                <label className="inline-flex cursor-pointer items-center gap-2 pb-2 text-sm">
                    <input
                        type="checkbox"
                        name="differentOnly"
                        checked={isDifferentOnly(filter.differentOnly)}
                        className={KEYWORD_MAPPING_FOCUS_RING}
                        onChange={onDifferentOnlyChange}
                    />
                    Only search intents with different H1 texts
                </label>
            </div>
            <TableList
                {...listState}
                definitions={tableDefinitions}
                renderRow={renderRow}
                addListFeatures
                hideSearch
                hideExport
                hideOrder
            />
        </>
    )
}

export default KeywordMappingSearchIntents
